// filepath: apps/web/src/features/lists/kanban/KanbanCard.tsx
//
// FR-UI-VIEW-1 AC-1 / AC-2 — single-record Kanban card.
//
// Renders ONE record as a draggable surface. The card's body iterates
// ONLY the keys the server returned in `record.fields` — INV-2
// (server-stripped rendering): if the caller's RBAC role excludes a
// field, that field is absent on the wire and absent here. The card
// makes NO attempt to fetch fields that didn't arrive.
//
// SECURITY / INVARIANT REMINDERS:
//   - INV-2 / FR-AUTH-7 — render exactly what the server returned.
//     The card iterates `Object.keys(record.fields)` and stops.
//   - INV-9 — every value is rendered as a text node (React's default
//     escaping). No `dangerouslySetInnerHTML`, no HTML interpretation.
//     A value `<img src=x onerror="alert(1)">` becomes the literal
//     text on screen, not an <img> tag.
//   - The card is a plain `<article>` with `draggable={true}`; the
//     drag-drop contract lives in `useKanbanDragDrop` — the card is
//     a passive surface that fires `onDragStart(event, payload)`.
//
// FIELD-SELECTION POLICY
//   Per AC-1 + AC-2 the card surfaces "fields defined in
//   `WireFieldDefinition[]`" — that is the definition-driven field set
//   the server authorised. We intersect that list with the keys the
//   server actually emitted (INV-2) and render the intersection in
//   DEFINITION order (so the column ordering stays stable across
//   renders, and the rendered columns mirror the table view's column
//   order). When a definition is absent (rare — the parent always
//   provides one), we fall back to `Object.keys(record.fields)` in the
//   server's emit order. The intersection always wins because
//   "definition says field X is addressable but server omitted it" is
//   a load-bearing safety boundary — it means the field is now
//   hidden, regardless of definition sort order.

import React from "react";
import type { NormalisedRecord, WireFieldDefinition } from "../types";
import { Badge } from "@/design-system/Badge";

/**
 * Kanban-specific record shape. Mirrors `NormalisedRecord` and adds
 * `rowVersion` so the drag-drop payload can carry it forward (INV-7 —
 * the row_version is required at PUT time and MUST come from the
 * server's emit, never client-computed).
 */
export interface KanbanRecord extends NormalisedRecord {
  /**
   * The `row_version` the server attached to the most-recent
   * fetch of this record. Surfaced into the drag-drop payload so
   * the drop handler can issue the PUT with the correct OCC token.
   * INV-7 — must come from the server.
   */
  rowVersion: number;
}

export interface KanbanCardProps {
  /**
   * The record to render. Server-stripped — `fields` contains only
   * the keys the caller may read (INV-2).
   */
  record: KanbanRecord;
  /**
   * Definition-driven field order. When supplied, the card renders
   * fields in this order; when omitted, falls back to
   * `Object.keys(record.fields)` (the server's emit order).
   */
  fields?: readonly WireFieldDefinition[];
  /**
   * Optimistic column value. When set, the card renders the parent
   * state (post-drag column value); when undefined, uses
   * `record.fields[groupByField]` (the server's current value).
   * Used by the parent during the in-flight drop animation so the
   * card renders in the destination column immediately.
   */
  columnValue?: string;
  /**
   * The enum field that drives column grouping. Captured in the
   * drag-drop payload so the drop handler can compose the next
   * PUT body. The card hides this field in the body (it would be
   * redundant with the column chip).
   */
  groupByField: string;
  /**
   * Fired when a drag starts on the card. Wired from
   * `useKanbanDragDrop.onCardDragStart`.
   */
  onDragStart: (
    event: React.DragEvent<HTMLElement>,
    payload: {
      recordId: string;
      fromValue: string;
      groupByField: string;
      rowVersion: number;
    },
  ) => void;
  /** Test hook forwarded onto the rendered root. */
  "data-testid"?: string;
}

/**
 * Resolve the optimal field order for the card body. Returns the
 * array of field names to render, in render order.
 */
function resolveFieldOrder(
  record: KanbanRecord,
  fields: readonly WireFieldDefinition[] | undefined,
): string[] {
  const emitOrder = Object.keys(record.fields ?? {});
  if (!fields || fields.length === 0) return emitOrder;
  // Walk definition order — include only fields the server emitted
  // (INV-2). When a definition field is missing from the record, it
  // is silently skipped; the definition's "declared fields" do not
  // override the server's "what the caller may read".
  const definitionOrder: string[] = [];
  for (const f of fields) {
    if (
      record.fields !== undefined &&
      Object.prototype.hasOwnProperty.call(record.fields, f.name)
    ) {
      definitionOrder.push(f.name);
    }
  }
  return definitionOrder.length > 0 ? definitionOrder : emitOrder;
}

/**
 * Format a record value for display. Strings render as-is; numbers
 * / booleans are coerced via `String()`; everything else falls
 * through to `JSON.stringify` (defensive — DRF-style null / array /
 * object values are not common in entity records but may appear in
 * custom user fields). The result is the body of a React text node
 * so React handles the escaping (INV-9).
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "[unserializable]";
  }
}

/**
 * KanbanCard — single-record draggable surface.
 *
 * Layout:
 *   <article draggable>
 *     <header>
 *       <Badge>{groupByField value}</Badge>
 *     </header>
 *     <ul> per field — text node only </ul>
 *   </article>
 */
export function KanbanCard({
  record,
  fields,
  columnValue,
  groupByField,
  onDragStart,
  "data-testid": testId = "kanban-card",
}: KanbanCardProps): React.ReactElement {
  const displayValue =
    columnValue ?? formatValue(record.fields?.[groupByField]);
  const order = resolveFieldOrder(record, fields);
  // Hide the group_by field in the body — the column chip at the
  // header carries it. This avoids "Status: open" repeated twice on
  // a status column card.
  const bodyFields = order.filter((n) => n !== groupByField);

  return (
    <article
      // FR-UI-OPS-3 carryover — the Card primitive returns a <div>.
      // We render an <article> directly with the same token-driven
      // styling so the surface remains semantically article (a
      // self-contained piece of content) while staying visually
      // consistent with the design-system Card primitive.
      data-testid={`${testId}-${record.id}`}
      data-kanban-card-id={record.id}
      draggable
      onDragStart={(event) =>
        onDragStart(event, {
          recordId: record.id,
          fromValue: displayValue,
          groupByField,
          // INV-7 — the card carries the row_version it observed at
          // the most-recent successful fetch. Kanban drag-drop
          // is a group-by update; the server enforces OCC on this
          // exact row_version.
          rowVersion: record.rowVersion,
        })
      }
      role="listitem"
      aria-label={`Card ${record.id}`}
      className="kanban-card"
      style={{
        marginBottom: "8px",
        cursor: "grab",
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-sm)",
        padding: "var(--space-3)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-sm)",
        color: "var(--text-primary)",
      }}
    >
      <header
        className="kanban-card-header"
        data-testid={`${testId}-${record.id}-header`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <Badge tone="primary" data-testid={`${testId}-${record.id}-column`}>
          {displayValue}
        </Badge>
      </header>
      <ul
        className="kanban-card-body"
        data-testid={`${testId}-${record.id}-body`}
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        {bodyFields.map((name) => {
          // INV-9 — the value is the body of a text node.
          const value = formatValue(record.fields?.[name]);
          return (
            <li
              key={name}
              data-testid={`${testId}-field-${name}`}
              data-field-name={name}
              className="kanban-card-field"
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "baseline",
              }}
            >
              <strong
                style={{
                  fontFamily: "var(--font-sans)",
                  fontWeight: "var(--font-medium)",
                  color: "var(--text-tertiary)",
                  fontSize: "var(--text-xs)",
                  minWidth: "80px",
                }}
              >
                {name}
              </strong>
              <span style={{ color: "var(--text-body)" }}>{value}</span>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
